<template>
    <Teleport to="body">
        <Transition name="sheet">
            <div v-if="open" role="dialog" aria-modal="true" aria-label="More menu" class="fixed inset-0 z-30">
                <button type="button" class="absolute inset-0 bg-black/40 w-full cursor-default" aria-label="Close menu" @click="$emit('close')" />
                <div
                    class="sheet-panel absolute bottom-0 inset-x-0 bg-nav-bg rounded-t-2xl"
                    :class="{ 'transition-transform': !isDragging }"
                    :style="sheetStyle"
                    @touchstart="onTouchStart"
                    @touchmove="onTouchMove"
                    @touchend="onTouchEnd">
                    <div class="flex justify-center pt-3 pb-2 cursor-grab">
                        <div class="w-10 h-1 rounded-full bg-border-strong" />
                    </div>

                    <nav class="px-6 pb-2">
                        <template v-for="item in items" :key="item.id">
                            <ValencerInstanceSwitcher v-if="item.id === 'logout' && instanceSwitcher" :show-labels="true" presentation="modal" />
                            <component
                                :is="itemTag(item)"
                                v-bind="itemAttrs(item)"
                                class="sheet-item"
                                :class="{ 'sheet-item-active': isItemActive(item) }"
                                @click="handleItemClick(item)">
                                <i
                                    v-if="'icon' in item && item.icon"
                                    class="bi text-lg"
                                    :class="[item.icon, isItemActive(item) ? 'text-nav-menu-active-text' : 'text-nav-icon']"
                                    aria-hidden="true" />
                                <span v-else-if="'iconComponent' in item && item.iconComponent" class="w-4.5 h-4.5"><component :is="item.iconComponent" /></span>
                                <span :class="isItemActive(item) ? 'body-semibold text-nav-menu-active-text' : 'body text-ink'">
                                    {{ item.title }}
                                </span>
                            </component>
                        </template>
                    </nav>

                    <form ref="logoutForm" method="POST" :action="logoutUrl" class="hidden">
                        <input type="hidden" name="csrfmiddlewaretoken" :value="getCookie('csrftoken')" />
                    </form>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { getCookie } from "/js/utils.js";
import { usePage } from "@inertiajs/vue3";
import { useEventListener } from "@vueuse/core";
import type { GlobalProperties, OrganizationDetails, StaffLink, UserDetails } from "~vue/appTypes";
import AlertCircle from "~vue/icons/AlertCircle.vue";
import Logout from "~vue/icons/Logout.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { isInIFrame } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import type { Component, ComputedRef } from "vue";
import { computed, inject, ref } from "vue";

import { COLLABORATION_PROFILE_BOOTSTRAP_ICON_CLASS, MainMenuPane } from "./menuPanes";
import ValencerInstanceSwitcher from "./ValencerInstanceSwitcher.vue";

const page = usePage<{ staff_links?: StaffLink[] }>();

interface SheetLinkItem {
    id: string;
    title: string;
    type: "link";
    icon: string;
    url: string;
}

interface SheetUrlItem {
    id: string;
    title: string;
    type: "url";
    url: string;
    icon?: string;
    iconComponent?: Component;
    target?: string;
}

interface SheetActionItem {
    id: string;
    title: string;
    type: "action";
    icon?: string;
    iconComponent?: Component;
    action: () => void;
}

type SheetItem = SheetLinkItem | SheetUrlItem | SheetActionItem;

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ close: []; openPrivacy: [] }>();

function onEscapeKey(e: KeyboardEvent) {
    if (e.key === "Escape" && props.open) {
        emit("close");
    }
}
useEventListener("keydown", onEscapeKey);

const isDragging = ref(false);
const translateY = ref(0);
let touchStartY = 0;

function onTouchStart(e: TouchEvent) {
    touchStartY = e.touches[0].clientY;
    translateY.value = 0;
    isDragging.value = true;
}

function onTouchMove(e: TouchEvent) {
    const dy = e.touches[0].clientY - touchStartY;
    if (dy > 0) {
        translateY.value = dy;
    }
}

function onTouchEnd() {
    const shouldClose = translateY.value > 80;
    isDragging.value = false;
    translateY.value = 0;
    if (shouldClose) {
        emit("close");
    }
}

const sheetStyle = computed(() => ({
    transform: translateY.value > 0 ? `translateY(${translateY.value}px)` : undefined,
}));

const globalProperties = inject<GlobalProperties>("globalProperties")!;
const userDetails = inject<ComputedRef<UserDetails>>("user_details")!;

const instanceSwitcher = computed(() => userDetails.value?.valencer_instance_switcher ?? null);
const staffItems = computed<SheetItem[]>(() =>
    (page.props.staff_links ?? []).map((link) => ({
        id: `staff-${link.url}`,
        title: link.title,
        type: link.is_inertia ? "link" : "url",
        icon: link.icon,
        url: link.url,
    })),
);

const disabledPanes = inject("mobileDisabledPanes", ref<string[]>([]));
const organizationDetails = inject<ComputedRef<OrganizationDetails>>("organization_details")!;
const alignHost = inject<ComputedRef<string>>("align_host")!;
const logoutForm = ref<HTMLFormElement | null>(null);
const logoutUrl = isInIFrame() ? "/accounts/logout/?next=/microsoft-teams/login/" : "/accounts/logout/";

function handleLogout() {
    logoutForm.value?.submit();
}

function itemTag(item: SheetItem) {
    if (item.type === "link") {
        return BaseLink;
    }
    if (item.type === "url") {
        return "a";
    }
    return "button";
}

function itemAttrs(item: SheetItem) {
    const base: Record<string, unknown> = { "data-testid": item.id };
    if (item.type === "link") {
        base.href = item.url;
    } else if (item.type === "url") {
        base.href = item.url;
        if (item.target) {
            base.target = item.target;
            base.rel = "noopener noreferrer";
        }
    } else {
        base.type = "button";
    }
    return base;
}

function handleItemClick(item: SheetItem) {
    if (item.id === "team-tools") {
        logUserInteraction(AmplitudeEventNames.MAIN_MENU_ITEM_CLICKED, { id: item.id, title: item.title });
    }
    if (item.id === MainMenuPane.PERFORMANCE_MANAGEMENT) {
        logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_ENTRY_CLICKED, { entry_source: "side_nav" });
    }
    if (item.type === "action") {
        item.action();
    } else {
        emit("close");
    }
}

function isItemActive(item: SheetItem): boolean {
    if (item.type === "action") {
        return false;
    }
    return page.url.startsWith(item.url);
}

const items = computed<SheetItem[]>(() => {
    const perfReviewLabel = organizationDetails?.value?.perf_review_custom_copy?.page_tab_label || "Performance Management";

    const allItems: SheetItem[] = [
        { id: MainMenuPane.GUIDED_SETUP, title: "Coaching setup", type: "link", icon: "bi-stars", url: "/onboarding/calendar-first/setup/" },
        { id: "profile", title: "Profile", type: "link", icon: "bi-person-circle", url: "/accounts/user-profile" },
        {
            id: MainMenuPane.COLLABORATION_PROFILE,
            title: "Collaboration Profile",
            type: "link",
            icon: COLLABORATION_PROFILE_BOOTSTRAP_ICON_CLASS,
            url: "/accounts/collaboration-profile",
        },
        { id: MainMenuPane.JOURNEYS, title: "Journeys", type: "link", icon: "bi-compass", url: "/journeys/" },
        { id: MainMenuPane.CONNECTIONS, title: "People", type: "link", icon: "bi-people", url: "/people/" },
        { id: MainMenuPane.PERFORMANCE_MANAGEMENT, title: perfReviewLabel, type: "link", icon: "bi-file-bar-graph", url: "/performance_management" },
        { id: MainMenuPane.REFLECTIONS, title: "Reflections", type: "link", icon: "bi-journal-text", url: "/reflections" },
        ...(userDetails?.value?.is_customer_demo_accounts_enabled
            ? ([{ id: "demo-accounts", title: "Demo Accounts", type: "link", icon: "bi-person-badge", url: "/demo-accounts/" }] satisfies SheetItem[])
            : []),
        ...(userDetails?.value?.can_see_client_analytics
            ? ([{ id: "analytics", title: "Analytics", type: "link", icon: "bi-bar-chart", url: "/client-analytics/" }] satisfies SheetItem[])
            : []),
        ...(organizationDetails?.value?.team_tools_link_enabled
            ? ([{ id: "team-tools", title: "Valence Team Tools", type: "url", url: alignHost.value, icon: "bi-box-arrow-up-right", target: "_blank" }] satisfies SheetItem[])
            : []),
        { id: "preferences", title: "Preferences", type: "link", icon: "bi-sliders", url: "/accounts/user-preferences" },
        {
            id: "privacy",
            title: organizationDetails?.value?.privacy_link_label || "Privacy",
            type: "action",
            icon: "bi-shield-lock",
            action: () => {
                emit("close");
                emit("openPrivacy");
            },
        },
        {
            id: "feedback",
            title: "Report an Issue",
            type: "url",
            url: isInIFrame() ? "/microsoft-teams/report-issue/" : globalProperties.$feedbackMailto,
            iconComponent: AlertCircle,
        },
        ...staffItems.value,
        { id: "logout", title: "Logout", type: "action", iconComponent: Logout, action: handleLogout },
    ];

    return allItems.filter((item) => {
        if (item.type === "link" && Object.values(MainMenuPane).includes(item.id as MainMenuPane)) {
            return !disabledPanes.value.includes(item.id);
        }
        return true;
    });
});
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

.sheet-item {
    @apply flex items-center gap-4 px-3 py-3.5 rounded-xl hover:bg-nav-sheet-hover-bg transition-colors w-full text-left;
}

.sheet-item-active {
    @apply bg-nav-menu-active-bg;
}

.sheet-panel {
    padding-bottom: max(1.5rem, env(safe-area-inset-bottom));
}

.sheet-enter-active {
    transition: opacity 200ms ease-out;

    & > :last-child {
        transition: transform 300ms cubic-bezier(0.33, 1, 0.68, 1);
    }
}

.sheet-leave-active {
    transition: opacity 150ms ease-in;

    & > :last-child {
        transition: transform 200ms ease-in;
    }
}

.sheet-enter-from,
.sheet-leave-to {
    opacity: 0;

    & > :last-child {
        transform: translateY(100%);
    }
}
</style>
